import { compact } from 'lodash';
import type { Pagination, TrueOrFalse } from '@/type';
import {
  type RenewalReview,
  RenewalReviewType,
  type ReviewItem,
} from '@/type/review';
import { get, patch, post, put } from '../axios';
import type { CreateFreeOrderReviewReqDto } from './dto/request/create-free-order-review.req.dto';
import type { CreateSubscriptionReviewReqDto } from './dto/request/create-subscription-review.req.dto';
import type { GetOrderItemReviewListReqDto } from './dto/request/get-order-item-review-list.req.dto';
import type { UpdateFreeOrderReviewReqDto } from './dto/request/update-free-order-review.req.dto';
import type { UpdateSubscriptionReviewReqDto } from './dto/request/update-subscription-review.req.dto';
import type { UpsertSubscriptionItemReviewsReqDto } from './dto/request/upsert-subscription-item-reviews.req.dto';
import type { CreateSubscriptionReviewRes } from './dto/response/create-subscription-review.res.dto';
import type { FreeOrderReviewResponse } from './dto/response/free-order-review.res.dto';
import type { OrderItemReviewListResponse } from './dto/response/order-item-review-list.res.dto';
import type { OrderItemReviewSummaryResponse } from './dto/response/order-item-review-summary.res.dto';
import type { ReviewsRatingResponse } from './dto/response/reviews-rating.res.dto';
import type { GetSubscriptionReviewRes } from './dto/response/subscription-review.res.dto';
import type { UpdateSubscriptionReviewRes } from './dto/response/update-subscription-review.res.dto';

/**
 * 리뷰(Reviews) API 클래스
 * 모든 리뷰 관련 기능을 제공합니다: 생성/수정/조회
 */
export class ReviewsApi {
  // 베스트 후기 응답에서 isBest=true만 골라낼 수 있도록 한 번에 충분히 큰 페이지를 받는다.
  private static readonly BEST_REVIEW_FETCH_PAGE_SIZE = 10;

  static readonly queryKeys = {
    // 리뷰 평균 평점 조회
    averageRating: () => ['/reviews/rating'],

    // OrderItem 리뷰 조회 (GET 요청만)
    orderItemReviewSummary: (
      orderItemId: number,
      params: {
        includeGroupOptions: TrueOrFalse;
        deliveryDate?: string;
        isSubscription?: TrueOrFalse;
      },
    ) => [`/reviews/order-item/${orderItemId}/summary`, params],
    orderItemReviewList: (
      orderItemId: number,
      params?: GetOrderItemReviewListReqDto,
    ) => compact([`/reviews/order-item/${orderItemId}`, params]),

    // 어글리어스 배송 리뷰 단건 조회
    freeOrderReview: (orderId: number, orderItemId: number) => [
      `/reviews/free-order/order/${orderId}/order-item/${orderItemId}`,
    ],

    // 정기배송 리뷰 단건 조회
    subscriptionReview: (orderId: number) => [
      `/reviews/subscription/order/${orderId}/v2`,
    ],

    // 정기배송 베스트 후기 N건 (/main 박스 후기 영역용)
    bestSubscriptionReviews: (limit: number) =>
      ['/subscription/review', 'best', limit] as const,
  };

  /** 정기배송 박스 후기 생성 (V2) */
  static async createSubscriptionReviewV2({
    orderId,
    dto,
  }: {
    orderId: number;
    dto: CreateSubscriptionReviewReqDto;
  }): Promise<CreateSubscriptionReviewRes> {
    const { data } = await post<CreateSubscriptionReviewRes>(
      `/reviews/subscription/order/${orderId}/v2`,
      dto,
    );
    return data;
  }

  /** 정기배송 박스 후기 수정 (V2) */
  static async updateSubscriptionReview({
    orderId,
    dto,
  }: {
    orderId: number;
    dto: UpdateSubscriptionReviewReqDto;
  }): Promise<UpdateSubscriptionReviewRes> {
    const { data } = await patch<UpdateSubscriptionReviewRes>(
      `/reviews/subscription/order/${orderId}/v2`,
      dto,
    );
    return data;
  }

  /**
   * 마켓 리뷰 생성
   * - 적립금 혜택 없음
   */
  static async createFreeOrderReview({
    orderId,
    dto,
  }: {
    orderId: number;
    dto: CreateFreeOrderReviewReqDto;
  }): Promise<void> {
    const { data } = await post<void>(
      `/reviews/free-order/order/${orderId}`,
      dto,
    );
    return data;
  }

  /**
   * 마켓 리뷰 수정
   */
  static async updateFreeOrderReview({
    orderId,
    dto,
  }: {
    orderId: number;
    dto: UpdateFreeOrderReviewReqDto;
  }): Promise<void> {
    const { data } = await patch<void>(
      `/reviews/free-order/order/${orderId}`,
      dto,
    );
    return data;
  }

  /**
   * OrderItem 후기 요약 조회
   * orderItem.productCatalogId로 후기 전체 조회 후 집계
   */
  static async getOrderItemReviewSummary(
    orderItemId: number,
    params: {
      includeGroupOptions: TrueOrFalse;
      deliveryDate?: string;
      isSubscription?: TrueOrFalse;
    },
  ): Promise<OrderItemReviewSummaryResponse> {
    const { data } = await get<OrderItemReviewSummaryResponse>(
      `/reviews/order-item/${orderItemId}/summary`,
      { params },
    );
    return data;
  }

  /**
   * OrderItem 전체 후기 조회
   * 페이징, 필터링, 정렬 지원
   */
  static async getOrderItemReviewList({
    orderItemId,
    params,
  }: {
    orderItemId: number;
    params?: GetOrderItemReviewListReqDto;
  }): Promise<OrderItemReviewListResponse> {
    const { data } = await get<OrderItemReviewListResponse>(
      `/reviews/order-item/${orderItemId}`,
      { params },
    );
    return data;
  }

  /**
   * 어글리어스 배송 리뷰 단건 조회
   * 특정 주문의 특정 주문 아이템에 대한 리뷰를 조회합니다.
   */
  static async getFreeOrderReview({
    orderId,
    orderItemId,
  }: {
    orderId: number;
    orderItemId: number;
  }): Promise<FreeOrderReviewResponse> {
    const { data } = await get<FreeOrderReviewResponse>(
      `/reviews/free-order/order/${orderId}/order-item/${orderItemId}`,
    );
    return data;
  }

  /** 정기배송 후기 조회 */
  static async getSubscriptionReview(
    orderId: number,
  ): Promise<GetSubscriptionReviewRes> {
    const { data } = await get<GetSubscriptionReviewRes>(
      `/reviews/subscription/order/${orderId}/v2`,
    );
    return data;
  }

  /** 정기배송 품목별 후기 생성/수정 (V2) - 서버에서 생성/수정 여부 결정 */
  static async upsertSubscriptionItemReviews({
    orderId,
    dto,
  }: {
    orderId: number;
    dto: UpsertSubscriptionItemReviewsReqDto;
  }): Promise<void> {
    await put<void>(`/reviews/subscription/order/${orderId}/order-item`, dto);
  }

  /**
   * 리뷰 평균 평점 조회
   * 전체 리뷰와 집밥일기의 평균 평점 및 개수를 조회합니다.
   */
  static async getAverageRating(): Promise<ReviewsRatingResponse> {
    const { data } = await get<ReviewsRatingResponse>('/reviews/rating');
    return data;
  }

  /**
   * 정기배송 베스트 후기 목록 조회
   * /main 박스 후기 영역용. Retool에서 운영자가 isBest 토글로 큐레이션한 후기를
   * 응답 순서 그대로 앞에서 limit건만 추출합니다.
   */
  static async findBestSubscriptionReviews(
    limit: number,
  ): Promise<RenewalReview[]> {
    const { data } = await get<Pagination<ReviewItem>>('/subscription/review', {
      params: {
        type: RenewalReviewType.SUBSCRIPTION,
        page: 1,
        pageSize: ReviewsApi.BEST_REVIEW_FETCH_PAGE_SIZE,
      },
    });

    const reviews = data.items
      .map((item) => item.review)
      .filter((review): review is RenewalReview => !!review?.isBest);

    return reviews.slice(0, limit);
  }
}
